import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, message, Select, Space, Table, Tag } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { LineItemsEditor, type LineItem } from "../../components/LineItemsEditor";
import { MoneyInput } from "../../components/MoneyInput";
import { useApi } from "../../helpers/useApi";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useSuppliersQuery, useItemsQuery } from "../../helpers/queries";
import { fmtMVR } from "../../helpers/format";
import type { QuotationRequest } from "../../api-models";

// Mirrors rfq.go's unexported quotationComparisonRow/quotationComparisonSupplier response
// shape — those types aren't exported to api-models.ts since tygo only picks up exported
// Go types, so the shape is declared locally here instead.
interface ComparisonSupplierPrice {
  supplierQuotationId: number;
  supplierId: number;
  supplierName: string;
  unitPriceLaari: number;
  unitPriceMvrLaari: number;
}
interface ComparisonRow {
  quotationRequestLineId: number;
  itemId: number;
  qty: number;
  prices: ComparisonSupplierPrice[];
}

export default function RFQDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: suppliers = [] } = useSuppliersQuery();
  const { data: allItems = [] } = useItemsQuery();
  const itemById = (itemId?: number) => allItems.find((m) => m.id === itemId);
  const itemCode = (itemId?: number) => itemById(itemId)?.code ?? itemId;
  const itemName = (itemId?: number) => itemById(itemId)?.name;

  const isNew = id === "new";
  const [qr, setQr] = useState<QuotationRequest | null>(null);
  const [lines, setLines] = useState<LineItem[]>([]);
  const [comparison, setComparison] = useState<ComparisonRow[]>([]);

  const [quoteSupplierId, setQuoteSupplierId] = useState<number>();
  const [quotePrices, setQuotePrices] = useState<Record<number, number>>({});

  async function load() {
    if (isNew) return;
    const res = await request<QuotationRequest>({ url: `/qrequests/${id}` });
    setQr(res);
    setLines((res.lines ?? []).map((l) => ({ key: String(l.id), itemId: l.itemId, qty: l.qty })));
    const rows = await request<ComparisonRow[]>({ url: `/qrequests/${id}/comparison` });
    setComparison(rows);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canWrite = hasAnyGrant(user, "Quotations.Write");

  async function save() {
    const payload = { notes: "", lines: lines.filter((l) => l.itemId && l.qty).map((l) => ({ itemId: l.itemId, qty: l.qty })) };
    const created = await request<QuotationRequest>({ method: "POST", url: "/qrequests", data: payload });
    message.success("RFQ created.");
    navigate(`/procurement/rfqs/${created.id}`, { replace: true });
  }

  async function submitQuotation() {
    if (!quoteSupplierId) {
      message.error("Choose a supplier.");
      return;
    }
    const quoteLines = lines
      .filter((l) => quotePrices[Number(l.key)] !== undefined)
      .map((l) => ({ quotationRequestLineId: Number(l.key), unitPriceLaari: quotePrices[Number(l.key)] }));
    if (quoteLines.length === 0) {
      message.error("Enter at least one unit price.");
      return;
    }
    await request({ method: "POST", url: `/qrequests/${id}/quotations`, data: { supplierId: quoteSupplierId, lines: quoteLines } });
    message.success("Quotation recorded.");
    setQuoteSupplierId(undefined);
    setQuotePrices({});
    load();
  }

  async function selectQuotation(supplierQuotationId: number) {
    await request({ method: "POST", url: `/qrequests/${id}/select`, data: { supplierQuotationId } });
    message.success("Submitted for supplier-selection approval.");
    load();
  }

  async function createPO() {
    const po = await request<{ id: number }>({ method: "POST", url: `/qrequests/${id}/create-po` });
    message.success("Purchase order drafted.");
    navigate(`/procurement/pos/${po.id}`);
  }

  if (!isNew && !qr) return null;

  return (
    <div>
      <PageHeader
        title={isNew ? "New Quotation Request" : qr!.docNo}
        extra={
          <Space>
            {qr && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={qr.status} />
              </span>
            )}
            {isNew && canWrite && (
              <Button type="primary" onClick={save} loading={loading}>
                Save
              </Button>
            )}
            {!isNew && qr?.status === "AWARDED" && hasAnyGrant(user, "PO.Write") && (
              <Button type="primary" onClick={createPO} loading={loading} data-tour="rfq-create-po">
                Create purchase order
              </Button>
            )}
          </Space>
        }
      />
      <Card size="small" title="Items" style={{ marginBottom: 16 }}>
        <LineItemsEditor items={lines} onChange={setLines} readOnly={!isNew || !canWrite} />
      </Card>

      {!isNew && (qr?.status === "DRAFT" || qr?.status === "SENT") && canWrite && (
        <Card size="small" title="Record a supplier quotation" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              showSearch
              style={{ width: 260 }}
              placeholder="Supplier"
              options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
              value={quoteSupplierId}
              onChange={setQuoteSupplierId}
            />
            <Table
              rowKey="key"
              pagination={false}
              size="small"
              dataSource={lines.filter((l) => l.itemId)}
              columns={[
                { title: "Code", render: (_, r) => itemCode(r.itemId) },
                { title: "Item", render: (_, r) => itemName(r.itemId) },
                { title: "Qty", dataIndex: "qty" },
                {
                  title: "Unit price",
                  render: (_, r) => (
                    <MoneyInput
                      valueLaari={quotePrices[Number(r.key)]}
                      onChangeLaari={(v) => setQuotePrices((prev) => ({ ...prev, [Number(r.key)]: v }))}
                    />
                  ),
                },
              ]}
            />
            <Button onClick={submitQuotation} loading={loading}>
              Record quotation
            </Button>
          </Space>
        </Card>
      )}

      {comparison.length > 0 && (
        <Card size="small" title="Quotation comparison (MVR-converted)" style={{ marginBottom: 16 }} data-tour="rfq-comparison">
          <Table
            rowKey="quotationRequestLineId"
            pagination={false}
            size="small"
            dataSource={comparison}
            columns={[
              { title: "Code", render: (_, row) => itemCode(row.itemId) },
              { title: "Item", render: (_, row) => itemName(row.itemId) },
              { title: "Qty", dataIndex: "qty" },
              {
                title: "Supplier prices",
                render: (_, row) => (
                  <Space wrap>
                    {row.prices.map((p) => (
                      <Tag key={p.supplierQuotationId} color={qr?.selectedQuotationId === p.supplierQuotationId ? "green" : undefined}>
                        {p.supplierName}: {fmtMVR(p.unitPriceMvrLaari)}
                        {canWrite && (qr?.status === "DRAFT" || qr?.status === "SENT") && (
                          <Button size="small" type="link" onClick={() => selectQuotation(p.supplierQuotationId)} data-tour="rfq-select">
                            Select
                          </Button>
                        )}
                      </Tag>
                    ))}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      {!isNew && <ApprovalTimeline docType="RFQ" docId={Number(id)} refreshKey={qr?.status} onActed={load} />}
    </div>
  );
}
